import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../../lib/api";
import { fmtMoney } from "../../lib/format";
import { ErrorState } from "../../components/States";

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [queue, setQueue] = useState(null);
  const [openReports, setOpenReports] = useState(null);
  const [hosts, setHosts] = useState(null);
  const [drivers, setDrivers] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.adminStats().then(setStats).catch((e) => setError(e.message));
    api.adminListingQueue().then((r) => setQueue(r.length)).catch(() => setQueue(0));
    api.adminReports("?status=open&page_size=1").then((r) => setOpenReports(r.total)).catch(() => setOpenReports(0));
    api.adminHosts("?page_size=1").then((r) => setHosts(r.total)).catch(() => setHosts(0));
    api.adminDrivers("?page_size=1").then((r) => setDrivers(r.total)).catch(() => setDrivers(0));
  }, []);

  if (error) return <ErrorState body={error} />;

  // Loading state: 12 non-interactive skeletons with UNIQUE keys — never Links
  // (duplicate keys previously orphaned skeleton nodes in the DOM after load).
  if (!stats) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="admin-stats-loading">
        {Array.from({ length: 12 }, (_, i) => (
          <div key={`skel-${i}`} aria-hidden="true" data-testid="admin-stat-skeleton"
               className="card-dark p-5 animate-pulse">
            <div className="h-7 w-14 rounded bg-[#1F2328]" />
            <div className="h-3 w-20 rounded bg-[#18181B] mt-2" />
          </div>
        ))}
      </div>
    );
  }

  const cards = [
    ["Users", stats.users, "/admin/users"],
    ["Hosts", hosts ?? "…", "/admin/hosts"],
    ["Drivers", drivers ?? "…", "/admin/drivers"],
    ["Listings", stats.spots, "/admin/listings"],
    ["Bookings", stats.bookings, "/admin/bookings"],
    ["Paid bookings", stats.paid_bookings, "/admin/bookings?status=paid"],
    ["GMV", fmtMoney(stats.gmv), "/admin/payments"],
    ["Platform revenue", fmtMoney(stats.platform_revenue), "/admin/payments"],
    ["Host payouts", fmtMoney(stats.host_payouts), "/admin/payments"],
    ["Take rate", stats.take_rate, "/admin/analytics"],
    ["Pending review", queue ?? "…", "/admin/listings?status=pending_review"],
    ["Open reports", openReports ?? "…", "/admin/reports"],
  ];

  return (
    <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="admin-stats">
      {cards.map(([l, v, to]) => (
        <Link key={l} to={to} data-testid={`admin-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}
              className="card-dark p-5 group hover:border-[#E50914]/60 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#E50914]">
          <p className="font-display font-extrabold text-2xl">{v}</p>
          <p className="text-xs text-zinc-400 mt-0.5 flex items-center gap-1">{l}
            <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" /></p>
        </Link>
      ))}
    </div>
  );
}
